import { useTranslation } from 'react-i18next';
import PageHero from '../../../components/shared/PageHero';
import Sidebar from '../../../components/shared/Sidebar';
import { Globe, FileText, Home, Plane, CheckCircle2, Mail, ChevronRight } from 'lucide-react';
import { Link } from 'react-router-dom';

const requirements = [
  'Passeport valide (min. 6 mois de validité)',
  'Formulaire de candidature ESTAD complété',
  'Relevés de notes officiels (traduits si nécessaire)',
  'Preuve de niveau en français ou en anglais',
  'Justificatifs de ressources financières',
  'Assurance maladie internationale',
  'Lettre de motivation',
];

const services = [
  { icon: <FileText className="w-6 h-6" />, title: 'Support Visa', desc: 'Accompagnement dans la procédure de visa étudiant et les documents requis.' },
  { icon: <Home className="w-6 h-6" />, title: 'Logement', desc: 'Aide pour trouver un logement sur campus ou en ville.' },
  { icon: <Plane className="w-6 h-6" />, title: 'Accueil à l\'Arrivée', desc: 'Navette aéroport et programmes d\'orientation pour les nouveaux étudiants.' },
  { icon: <Globe className="w-6 h-6" />, title: 'Intégration Culturelle', desc: 'Programmes pour s\'adapter à la vie au Cameroun et rencontrer d\'autres étudiants.' }
];

export default function InternationalStudents() {
  const { t } = useTranslation();

  return (
    <div className="flex flex-col bg-white">
      <PageHero
        title={t('info_international_students', 'Étudiants Internationaux')}
        description="Rejoignez une communauté d'apprenants venue du monde entier. ESTAD accueille des étudiants de tous les pays pour étudier au Cameroun."
        breadcrumbs={[
          { label: t('nav_admissions', 'Admissions'), href: '/admissions' },
          { label: 'International', href: '/admissions/international' }
        ]}
        variant="light"
      />

      <main className="max-w-7xl mx-auto w-full py-12 md:py-20 px-4 sm:px-6 lg:px-12 flex flex-col lg:flex-row gap-12 lg:gap-16">
        <Sidebar variant="admissions" />

        <div className="flex-1 space-y-24 animate-in fade-in slide-in-from-right-8 duration-700 delay-200">

          {/* Hero Banner with image */}
          <section className="flex flex-col md:flex-row gap-12 items-center">
            <div className="flex-1 space-y-8">
              <span className="inline-block text-[10px] font-black uppercase tracking-[0.3em] text-gold border border-gold/30 px-3 py-1 rounded-full">
                20+ Nationalités
              </span>
              <h2 className="text-3xl md:text-4xl font-black text-navy uppercase tracking-tighter italic leading-tight">
                Votre éducation mondiale commence ici
              </h2>
              <p className="text-gray-600 font-medium leading-relaxed text-lg">
                ESTAD accueille des étudiants de plus de 20 pays, créant un environnement d'apprentissage multiculturel et dynamique. Notre équipe de services aux étudiants internationaux est là pour vous accompagner de la candidature à la diplomation.
              </p>
              <div className="flex flex-wrap gap-4 pt-4">
                {[
                  '20+ nationalités représentées',
                  'Programmes en français et anglais',
                  'Conseiller dédié aux étudiants internationaux',
                  'Support demande de visa'
                ].map((fact, idx) => (
                  <div key={idx} className="flex items-center gap-2 text-sm text-navy font-bold">
                    <CheckCircle2 className="w-4 h-4 text-gold flex-shrink-0" />
                    <span>{fact}</span>
                  </div>
                ))}
              </div>
              <div className="flex flex-wrap gap-4 pt-4">
                <Link to="/admissions" className="bg-navy text-white px-10 py-4 font-black uppercase tracking-widest text-xs hover:bg-gold hover:text-navy transition-all active:scale-95">
                  Postuler Maintenant
                </Link>
                <a href="mailto:international@estad.edu" className="border-2 border-navy text-navy px-10 py-4 font-black uppercase tracking-widest text-xs hover:bg-navy hover:text-white transition-all active:scale-95">
                  Bureau International
                </a>
              </div>
            </div>
            <div className="w-full md:w-[42%] aspect-[4/3] overflow-hidden rounded-sm shadow-2xl border-b-8 border-gold flex-shrink-0">
              <img src="/images/campus/students-01.jpg" alt="International Students" className="w-full h-full object-cover" />
            </div>
          </section>

          {/* Requirements */}
          <section id="requirements" className="space-y-12">
            <h2 className="text-3xl font-black text-navy uppercase tracking-tight border-b-2 border-gray-100 pb-4 flex items-center gap-4 italic">
              <FileText className="w-8 h-8 text-gold" /> Conditions d'Admission
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              {/* Required documents */}
              <div className="bg-gray-50 p-8 border-t-8 border-navy rounded-sm shadow-sm space-y-6">
                <h3 className="text-xl font-black text-navy uppercase tracking-tight">Documents Requis</h3>
                <ul className="space-y-4">
                  {requirements.map((req, idx) => (
                    <li key={idx} className="flex items-start gap-3 text-sm text-gray-600 font-medium">
                      <CheckCircle2 className="w-4 h-4 text-gold flex-shrink-0 mt-0.5" />
                      {req}
                    </li>
                  ))}
                </ul>
              </div>
              {/* Language requirements */}
              <div className="bg-gray-50 p-8 border-t-8 border-gold rounded-sm shadow-sm space-y-6">
                <h3 className="text-xl font-black text-navy uppercase tracking-tight">Niveau de Langue</h3>
                <p className="text-sm text-gray-600 font-medium leading-relaxed">
                  ESTAD propose des programmes en français et en anglais. Les étudiants internationaux doivent démontrer leur maîtrise dans la langue d'enseignement.
                </p>
                <div className="space-y-4 pt-2">
                  {[
                    { lang: 'Anglais', req: 'TOEFL (min 80) ou IELTS (min 6.0)' },
                    { lang: 'Français', req: 'DELF B2 ou TCF (min 400)' }
                  ].map((item, idx) => (
                    <div key={idx} className="flex gap-4 items-center p-4 bg-white border border-gray-100 group hover:border-gold transition-all rounded-sm">
                      <div className="w-2.5 h-2.5 rounded-full bg-gold flex-shrink-0"></div>
                      <div>
                        <span className="font-black text-navy text-sm uppercase tracking-tighter">{item.lang}: </span>
                        <span className="text-gray-500 text-sm font-medium">{item.req}</span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </section>

          {/* Services Grid */}
          <section id="services" className="space-y-12">
            <h2 className="text-3xl font-black text-navy uppercase tracking-tight border-b-2 border-gray-100 pb-4 flex items-center gap-4 italic">
              <Globe className="w-8 h-8 text-gold" /> Services aux Étudiants Internationaux
            </h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
              {services.map((service, idx) => (
                <div key={idx} className="group bg-gray-50 p-8 border-l-8 border-navy hover:bg-white hover:shadow-2xl hover:border-gold transition-all duration-300 rounded-sm space-y-4">
                  <div className="w-12 h-12 rounded-lg bg-white shadow-sm flex items-center justify-center text-navy group-hover:scale-110 transition-transform">
                    {service.icon}
                  </div>
                  <h3 className="text-lg font-black uppercase tracking-tight leading-none text-navy">{service.title}</h3>
                  <p className="text-gray-500 text-xs font-medium leading-relaxed">{service.desc}</p>
                </div>
              ))}
            </div>
          </section>

          {/* CTA Banner */}
          <section id="contact" className="bg-navy p-8 md:p-16 text-white rounded-sm shadow-2xl relative overflow-hidden group flex flex-col md:flex-row items-center justify-between gap-10">
            <div className="absolute bottom-0 right-0 w-64 h-64 bg-gold/5 rounded-full translate-y-1/2 translate-x-1/2 group-hover:scale-110 transition-transform duration-1000 pointer-events-none"></div>
            <div className="relative z-10 space-y-4">
              <h2 className="text-3xl md:text-4xl font-black uppercase tracking-tighter italic leading-none">
                Des questions sur vos études à ESTAD ?
              </h2>
              <p className="text-white/60 font-medium">Nos conseillers internationaux sont disponibles pour vous aider.</p>
            </div>
            <div className="relative z-10 flex flex-wrap gap-4">
              <a href="mailto:international@estad.edu" className="bg-gold text-navy px-10 py-4 font-black uppercase tracking-widest text-xs shadow-xl hover:brightness-110 active:scale-95 transition-all flex items-center gap-2">
                <Mail className="w-4 h-4" /> Email
              </a>
              <Link to="/contact" className="border-2 border-white/30 text-white px-10 py-4 font-black uppercase tracking-widest text-xs hover:bg-white hover:text-navy transition-all active:scale-95 flex items-center gap-2">
                Contact <ChevronRight className="w-4 h-4" />
              </Link>
            </div>
          </section>

        </div>
      </main>
    </div>
  );
}
